import React, { useRef, useState, useContext } from 'react'
import {
  Route,
  Link as RouterLink,
  withRouter,
  useLocation,
} from 'react-router-dom'
import {
  StackView,
  Button,
  Box,
  Icon,
  Text,
} from '@planningcenter/tapestry-react'
import store from '../../api/store'
import SearchField from '../search/field'
import Logo from '../shared/logo'
import { colors } from '../theme'
import { MenuContext, ActivePathContext } from './global_layout'

const NavLink = ({ to, selected, label }) => {
  return (
    <StackView distribution="end" height="100%">
      <Text
        fontWeight={700}
        color={colors['primary']}
        marginBottom={5}
        as={RouterLink}
        to={to}
        variant="naked"
        position="relative"
        top={0.25}
      >
        {label}
      </Text>
      <Box
        as="span"
        background={selected ? colors['primary'] : 'transparent'}
        width="100%"
        height="4px"
      />
    </StackView>
  )
}

const SearchForm = withRouter(({ location, history }) => {
  if (location.pathname.startsWith('/search')) return null

  const inputRef = useRef(null)
  const [value, setValue] = useState('')

  const search = event => {
    event.preventDefault()
    if (value === '') return

    let path = `/search/${encodeURIComponent(value)}`
    if (location.pathname.startsWith('/apps')) {
      path = `${path}/${location.pathname.split('/')[2]}`
    }
    history.push(path)

    if (inputRef.current) {
      inputRef.current.blur()
    }
    setValue('')
  }

  return (
    <Box
      style={{ flex: 1 }}
      marginRight={2}
      mediaQueries={{
        lg: {
          marginRight: 1,
        },
      }}
    >
      <form onSubmit={search}>
        <SearchField
          value={value}
          onChange={e => setValue(e.target.value)}
          ref={inputRef}
        />
      </form>
    </Box>
  )
})

const Header = props => {
  const lastApp = store.get('lastApp') || 'people'
  const { mobileMenuOpen, setMobileMenuOpen } = useContext(MenuContext)
  const { activeOverviewPath, activeReferencePath } =
    useContext(ActivePathContext)
  const location = useLocation()
  const { pathname } = location

  return (
    <StackView
      axis="vertical"
      alignment="center"
      distribution="space-between"
      columnStart={1}
      columnEnd={-1}
      row={1}
      paddingHorizontal={2}
      paddingTop={2}
      backgroundColor={colors['grey-1']}
      borderBottom={`1px solid ${colors['grey-5']}`}
      mediaQueries={{
        lg: {
          padding: 0,
          axis: 'horizontal',
        },
      }}
      {...props}
    >
      <StackView
        axis="vertical"
        height="auto"
        width="100%"
        mediaQueries={{
          lg: {
            axis: 'horizontal',
            height: '100%',
            width: 'auto',
          },
        }}
      >
        <StackView
          axis="horizontal"
          distribution="space-between"
          alignment="center"
          padding={2}
          width="100%"
          mediaQueries={{
            lg: {
              padding: 0,
              width: 'auto',
            },
          }}
        >
          <RouterLink to="/overview/">
            <Logo style={{ marginRight: '1em' }} />
          </RouterLink>
          <Box
            marginTop={-0.5}
            mediaQueries={{
              lg: {
                visible: false,
              },
            }}
          >
            <Button
              theme="plain"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Icon name="general.threeHorizontalBars" size="md" />
              <Text
                marginLeft={1}
                weight={700}
                visible={false}
                mediaQueries={{
                  xs: {
                    visible: true,
                  },
                }}
              >
                Menu
              </Text>
            </Button>
          </Box>
        </StackView>

        <Route>
          {({ location }) => {
            const getActiveLinkClass = path =>
              location.pathname.startsWith(`${path}`)
            const getOverviewPath = () => {
              if (pathname.includes('search')) {
                return '/overview/'
              }
              return activeOverviewPath || `/overview/`
            }
            const getActiveReferencePath = () => {
              return activeReferencePath || `/apps/${lastApp}`
            }
            return (
              <StackView
                visible={false}
                mediaQueries={{
                  lg: {
                    visible: true,
                    axis: 'horizontal',
                    height: '100%',
                    spacing: 2,
                    marginLeft: 2,
                  },
                }}
              >
                <NavLink
                  to={getOverviewPath()}
                  selected={getActiveLinkClass('/overview')}
                  label="Overview"
                />

                <NavLink
                  to={getActiveReferencePath()}
                  selected={getActiveLinkClass('/apps')}
                  label="Reference"
                />
              </StackView>
            )
          }}
        </Route>
      </StackView>

      <StackView
        axis="horizontal"
        distribution="space-between"
        alignment="stretch"
        width="100%"
        padding={2}
        mediaQueries={{
          lg: {
            paddingTop: 3,
            width: 'auto',
            marginRight: 2,
          },
        }}
      >
        <SearchForm />
        <Button
          theme="primary"
          variant="fill"
          title="My Developer Account"
          to="https://api.planningcenteronline.com/oauth/applications"
          size="md"
          role="button"
        />
      </StackView>
    </StackView>
  )
}

export default Header
